import React from 'react';

import Button from '../../../../library/button/button';
import Quantity from '../../../../library/quantity/quantity';
import AddProductButton from '../../../../components/add-product-button/add-product-button';
import Price from '../../../../components/price/price';
import QuantityDisclaimer from '../../../../components/quantity-disclaimer/quantity-disclaimer';
import StickyFooter from '../../../appointment-page/components/sticky-footer/sticky-footer';
import {
  MinusRegularIcon,
  PlusRegularIcon
} from '../../../../shared/icons/icons';

import {
  useProductEntryDataLayerValues,
  useTireWheelPackage
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';

import { Fitment } from '../../../../shared/types/fitment';
import { ProductItem } from '../../../../shared/types/product';

import './sticky-add-to-cart-section.one-dt.scss';

interface StickyAddToCartSectionProps {
  className?: string;
  defaultQuantity: number;
  isCanonicalProduct?: boolean;
  isDisabled?: boolean;
  isSet?: boolean;
  isStaggered?: boolean;
  price: number;
  productItem: ProductItem;
  productType: string;
  selectedFitment?: Fitment;
  storeCode?: string | null;
  updateQuantity: (e: Event, quantity: number) => void;
}

function StickyAddToCartSection(props: StickyAddToCartSectionProps) {
  const {
    className = '',
    defaultQuantity,
    isCanonicalProduct = false,
    isDisabled = false,
    isSet = false,
    isStaggered = false,
    price,
    productItem,
    productType,
    selectedFitment,
    storeCode,
    updateQuantity
  } = props;

  const { prebuiltPackage, product, quantity } = productItem;
  const { mapDisplayRule, mapPrice, mapRuleSatisfied, priceRange, unitName } =
    product || {};

  const { currentTireWheelPackageFlow } = useTireWheelPackage();

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  let currQuantity = quantity || defaultQuantity;

  function handleIncrease(e) {
    const newValue = currQuantity + 1;

    if (updateQuantity) {
      updateQuantity(e, newValue);
    }
  }

  function handleDecrease(e) {
    const newValue = currQuantity - 1;

    if (updateQuantity) {
      updateQuantity(e, newValue);
    }
  }

  function getQtyAndPriceClassName() {
    let styleName = 'qty-and-price';

    if (isCanonicalProduct) {
      return styleName + '-canonical';
    } else if (currentTireWheelPackageFlow) {
      return styleName + '-byo-package';
    } else {
      return styleName + '-standard';
    }
  }

  return (
    <StickyFooter className={className} styleName="container">
      {!isStaggered && !isSet && (
        <div styleName={getQtyAndPriceClassName()}>
          <Price
            mapPrice={{
              mapPriceDisplay: mapDisplayRule?.mapPriceDisplay,
              mapRuleSatisfied,
              price: mapPrice?.value
            }}
            price={price}
            priceRange={priceRange}
            unitName={unitName}
            variant="P"
          />
          {(selectedFitment && isCanonicalProduct && !priceRange) ||
          !isCanonicalProduct ? (
            <>
              {currentTireWheelPackageFlow ? (
                <div styleName="static-quantity">
                  <Quantity
                    aria-label="static-quantity"
                    defaultValue={defaultQuantity}
                    id="quantity"
                    isDisabled
                    isHorizontal
                    max={99}
                    min={1}
                  />
                </div>
              ) : (
                <div styleName="quantity-buttons">
                  <Button
                    isDisabled={currQuantity < 2}
                    onClick={handleDecrease}
                    styleName={currQuantity < 2 ? 'button-disabled' : 'button'}
                  >
                    <MinusRegularIcon />
                  </Button>

                  <Quantity
                    aria-label="quantity-input"
                    defaultValue={defaultQuantity}
                    id="quantity"
                    isHorizontal
                    max={99}
                    min={1}
                    onBlur={updateQuantity}
                    onChange={updateQuantity}
                    value={currQuantity}
                  />

                  <Button
                    isDisabled={currQuantity > 98}
                    onClick={handleIncrease}
                    styleName={currQuantity > 98 ? 'button-disabled' : 'button'}
                  >
                    <PlusRegularIcon />
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      <AddProductButton
        hideSubtotal
        isDisabled={isDisabled}
        onClick={() =>
          trackAction(
            'product_detail_cart_add',
            getProductEntryDataLayerValues(product, currQuantity)
          )
        }
        productItem={productItem}
        storeCode={storeCode}
      />

      {!prebuiltPackage && !isStaggered && (
        <QuantityDisclaimer
          productType={productType}
          quantity={currQuantity}
          styleName="quantity-disclaimer"
        />
      )}
    </StickyFooter>
  );
}

export default StickyAddToCartSection;
